import accountFormModel from '@/model/sales-rep/AccountForm.model';
import AccountFormContext from './AccountFormConext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  deleteAccountForm,
  getAllAccountForm,
  getAllStates,
  isProntoCodeUnique,
  saveAccountForm,
  saveAndSendMailAccountForm,
  updatedAccountForm,
  updatedAndSendMailAccountForm,
  downloadAccountFromPdf,
} from '@/services/accountFormService';
import { ConfirmAlert, showAlert } from '@/components';
import { useLoading } from '@/utils/LoadingUtil';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/lib/handleApiError';

const AccountFormContextProvider = ({ children }) => {
  const { accountForm, setAccountForm, initialState } = accountFormModel();
  const [errors, setErrors] = useState({});
  const [isProntoUnique, setIsProntoUnique] = useState(false);
  const [accountFormData, setAccountFormData] = useState([]);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const nav = useNavigate();

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(10);
    setSortBy('');
    setSortDir('desc');
  }, [nav]);

  //Get All States
  const getAllStatesAPI = async () => {
    try {
      const response = await getAllStates();
      return response;
    } catch (error) {
      handleApiError('error', error.message, error?.response?.status);
    }
  };

  const createAccountForm = async (data, closeModal) => {
    startLoading();
    try {
      const response = await saveAccountForm(data);
      showAlert('success', 'Account Form Created Successfully');
      await getAccountFormAPI();
      clearFormData();
      closeModal();
      return response;
    } catch (error) {
      console.log(error);
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorMessages = Object.values(error.response.data).join(', ');
        handleApiError('error', errorMessages, error?.response?.status);
      } else {
        handleApiError(
          'error',
          error.response?.data || 'Something went wrong',
          error?.response?.status,
        );
      }
    } finally {
      stopLoading();
    }
  };

  const saveAndSendAccountForm = async (data, closeModal) => {
    startLoading();
    try {
      const response = await saveAndSendMailAccountForm(data);
      showAlert('success', 'Account Form Created Successfully, and email send');
      await getAccountFormAPI();
      clearFormData();
      closeModal();
      return response;
    } catch (error) {
      console.log(error);
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorMessages = Object.values(error.response.data).join(', ');
        handleApiError('error', errorMessages, error?.response?.status);
      } else {
        handleApiError(
          'error',
          error.response?.data || 'Something went wrong',
          error?.response?.status,
        );
      }
    } finally {
      stopLoading();
    }
  };

  const updateAccountForm = async (data, id, closeModal) => {
    startLoading();
    try {
      const response = await updatedAccountForm(data, id);
      showAlert('success', 'Account Form Updated Successfully');
      clearFormData();
      closeModal();
      getAccountFormAPI();
      return response;
    } catch (error) {
      console.log(error);
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorMessages = Object.values(error.response.data).join(', ');
        handleApiError('error', errorMessages, error?.response?.status);
      } else {
        handleApiError(
          'error',
          error.response?.data || 'Something went wrong',
          error?.response?.status,
        );
      }
    } finally {
      stopLoading();
    }
  };
  const updateAndSendMailAccountForm = async (data, id, closeModal) => {
    startLoading();
    try {
      const response = await updatedAndSendMailAccountForm(data, id);
      showAlert(
        'success',
        'Account form updated successfully, and email sent.',
      );
      clearFormData();
      closeModal();
      getAccountFormAPI();
      return response;
    } catch (error) {
      console.log(error);
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorMessages = Object.values(error.response.data).join(', ');
        handleApiError('error', errorMessages, error?.response?.status);
      } else {
        handleApiError(
          'error',
          error.response?.data || 'Something went wrong',
          error?.response?.status,
        );
      }
    } finally {
      stopLoading();
    }
  };

  const checkProntoCodeUnique = async () => {
    setIsProntoUnique(false);

    if (
      !accountForm.prontoAccountCode.trim() ||
      accountForm.prontoAccountCode === ''
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        prontoAccountCode: 'Pronto Code is required',
      }));
      return;
    }

    try {
      const response = await isProntoCodeUnique(accountForm.prontoAccountCode);
      if (response == true) {
        setIsProntoUnique(true);
        setErrors((prevErrors) => ({
          ...prevErrors,
          prontoAccountCode: 'Pronto Code is unique',
        }));
      } else {
        setIsProntoUnique(false);
        setErrors((prevErrors) => ({
          ...prevErrors,
          prontoAccountCode: 'Pronto Code already exists',
        }));
      }
    } catch (error) {
      setIsProntoUnique(false);
      handleApiError(
        'error',
        error.message || 'Something went wrong',
        error?.response?.status,
      );
    }
  };

  const getAccountFormAPI = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      startLoading();
      try {
        const response = await getAllAccountForm(
          page,
          size,
          search,
          sortField, // Use renamed parameter
          sortDirection, // Use renamed parameter
        );
        if (response.status === 204) {
          setAccountFormData(['No Data Found']);
          setTotalElements(0);
          return;
        }
        setAccountFormData(
          response.data._embedded?.newAccountFormResponseList || [],
        );
        setTotalElements(response?.data?.page?.totalElements || 0);
      } catch (error) {
        handleApiError('error', error.message, error?.response?.status);
        setTotalElements(0);
        setAccountFormData([]);
      } finally {
        stopLoading();
      }
    },
    [searchQuery, startLoading, stopLoading],
  );

  const deleteAccountFormAPI = async (id) => {
    const result = await ConfirmAlert({
      confirmBtnText: 'Delete Account Form',
    });
    if (!result.isConfirmed) {
      showAlert('error', 'Account Form deletion cancelled');
      return;
    }

    startLoading();
    try {
      const response = await deleteAccountForm(id);
      showAlert('success', 'Account Form Deleted Successfully');
      getAccountFormAPI();
      return response;
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const getAccountFormPdfAPI = async (id) => {
    startLoading();
    try {
      const response = await downloadAccountFromPdf(id);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `account_form_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showAlert('success', 'Account Form PDF downloaded successfully');
    } catch (error) {
      handleApiError(
        'error',
        error.message || 'Something went wrong while downloading the PDF',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  };

  const clearFormData = () => {
    setAccountForm(initialState);
    setErrors({});
  };

  const contextValue = useMemo(
    () => ({
      accountForm,
      setAccountForm,
      initialState,
      errors,
      setErrors,
      getAllStatesAPI,
      createAccountForm,
      checkProntoCodeUnique,
      isProntoUnique,
      getAccountFormAPI,
      updateAccountForm,
      clearFormData,
      startLoading,
      stopLoading,
      isLoading,
      accountFormData,
      setAccountFormData,
      deleteAccountFormAPI,
      setCurrentPage,
      setPageSize,
      totalElements,
      setTotalElements,
      currentPage,
      pageSize,
      searchQuery,
      setSearchQuery,
      saveAndSendAccountForm,
      updateAndSendMailAccountForm,
      getAccountFormPdfAPI,
      sortBy,
      setSortBy,
      sortDir,
      setSortDir,
    }),
    [
      accountForm,
      setAccountForm,
      initialState,
      errors,
      setErrors,
      getAllStatesAPI,
      createAccountForm,
      checkProntoCodeUnique,
      isProntoUnique,
      getAccountFormAPI,
      updateAccountForm,
      clearFormData,
      startLoading,
      stopLoading,
      isLoading,
      accountFormData,
      setAccountFormData,
      deleteAccountFormAPI,
      setCurrentPage,
      setPageSize,
      totalElements,
      setTotalElements,
      currentPage,
      pageSize,
      searchQuery,
      setSearchQuery,
      saveAndSendAccountForm,
      updateAndSendMailAccountForm,
      getAccountFormPdfAPI,
      sortBy,
      setSortBy,
      sortDir,
      setSortDir,
    ],
  );

  return (
    <AccountFormContext.Provider value={contextValue}>
      {children}
    </AccountFormContext.Provider>
  );
};

export default AccountFormContextProvider;
export const useAccountForm = () => useContext(AccountFormContext);
